"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, Volume2, VolumeX, Bell, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";

// Polymarket Elon tweet markets run Fri close → Fri close (22:00 UTC).
// The backend (/api/markets/elon-stats) only caches the *current* week —
// XTracker isn't queried for arbitrary ranges. So we expose a single market
// (the live week) and skip historical tabs that would mislead users with
// duplicated data. Compute dynamically so the dates never go stale.
const CURRENT_MARKET = (() => {
  const fmtIso = (d: Date) => d.toISOString().slice(0, 10);
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 22, 0, 0));
  let daysUntilFriday = (5 - end.getUTCDay() + 7) % 7;
  if (daysUntilFriday === 0 && now.getTime() >= end.getTime()) daysUntilFriday = 7;
  end.setUTCDate(end.getUTCDate() + daysUntilFriday);
  const startD = new Date(end);
  startD.setUTCDate(startD.getUTCDate() - 7);
  return {
    id: fmtIso(end),
    startDate: fmtIso(startD),
    endDate: fmtIso(end),
    endTime: end.toISOString(),
  };
})();

// Speed targets
const SPEED_TARGETS = [60, 80, 100, 120, 140, 160, 180, 200, 220, 240, 260, 280, 300, 320, 340, 360, 380, 400, 420, 440, 460, 480, 500, 520, 540, 560, 580, 600];

interface DailyMetric {
  date: string;
  tweets: number;
  replies: number;
  total: number;
  cumulative: number;
}

interface HourlyMetric {
  date: string;
  hour: string;
  tweets: number;
  replies: number;
  total: number;
}

interface ElonData {
  user: { handle: string; name: string };
  metrics: {
    daily: DailyMetric[];
    hourly: HourlyMetric[];
    totalTweets: number;
    totalWithReplies: number;
  };
  fetchedAt: string;
}

interface LiveTweet {
  id: string;
  content: string;
  timestamp: number;
  isReply: boolean;
  tweetNumber: number;
  xid?: string;
}

interface LiveSpeed {
  timeSinceLastActivity: number | null;
  activityCountInPeriod: number;
  avgTimeBetweenTweets: number | null;
  screenTime: number | null;
  firstActivityInPeriod: number | null;
  lastActivityInPeriod: number | null;
}

interface LiveData {
  count: number;
  tweets: LiveTweet[];
  latestTweet: LiveTweet | null;
  liveSpeed?: LiveSpeed;
}

export default function ElonDashboardPage() {
  const t = useT();
  const locale = useLocale();
  const activeMarket = CURRENT_MARKET;
  const [data, setData] = useState<ElonData | null>(null);
  const [liveData, setLiveData] = useState<LiveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(30);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newTweetAlert, setNewTweetAlert] = useState(false);
  const [includeReplies, setIncludeReplies] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const visRef = useVisibilityRef();

  const prevCountRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQYSj9LYqXYbEk2Y0NyijC4SR5jQ3KKMLRJIZ8/iqIUoEUhhzuyqjioQX2bQ5J+DCAI3RbDX2a6VTS8bN8vh2JNQC3CYzaWsm2EHOXPJy7WWdDQ5WqfJxKeoikoaY5q+w6qNRCAsZ6a0v66tfy8FQpnHr7ezpW0aPYi8sLGuoH9EO3jBt7OynH1DOGrGrr3ClnU3N2S9r7/Jm3pBMnbCqsPDlXxGM3K/qMXAl3tFL3C+pcjPl3xHLm66osrRl3xILm66ocrSlnxJLW25oMzUl3xJLG24n8zVl3xKK2y4nszVlnxLK2y3nc7XlXxLKmu2nM/XlXtMKmu2nM/YlHtNKWq1m8/ZlHtOKGq0ms/alHtPKGm0mc/blHtQJ2mzmc/clHtRJmizmc/clHtSJmeymM/dlXtTJWeymM/elntUJWaxl8/fl3tVJGawl8/gl3tWI2Wvls/hmHtXI2SvlM/imHtYImSuls/jmXtZIWSuls/jmnpaIGOtlM/jm3pbH2Ktk8/jnHpcH2GskM/knXtdHmGskM/knnteHWCrjs/ln3tfHWCqjM/loHtgHF+qjM/loXthG1+pjM/monta");
    return () => {
      if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    };
  }, []);

  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Fetch main data
  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ startDate: activeMarket.startDate, endDate: activeMarket.endDate });
      const res = await fetch(`/api/elontweets?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch data");
      const result = await res.json();
      setData(result);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarket]);

  // Fetch live data
  const fetchLiveData = useCallback(async () => {
    try {
      const res = await fetch("/api/elontweets/live");
      if (!res.ok) return;
      const result = await res.json();
      setLiveData(result);

      if (prevCountRef.current !== null && result.count > prevCountRef.current) {
        playSound();
        setNewTweetAlert(true);
        if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
        alertTimeoutRef.current = setTimeout(() => setNewTweetAlert(false), 5000);
      }
      prevCountRef.current = result.count;
      setCountdown(30);
    } catch (err) {}
  }, [playSound]);

  useEffect(() => {
    fetchData();
    fetchLiveData();
    const dataInterval = setInterval(() => { if (visRef.current) fetchData(); }, 30000);
    const liveInterval = setInterval(() => { if (visRef.current) fetchLiveData(); }, 30000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(liveInterval);
    };
  }, [fetchData, fetchLiveData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 30 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Time remaining calculation
  const getTimeRemaining = useCallback(() => {
    const now = new Date();
    const end = new Date(activeMarket.endTime);
    const diff = end.getTime() - now.getTime();
    if (diff <= 0) return { text: t("elon.tracker.ended"), days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0 };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    const dayLabel = days === 1 ? t("elon.tracker.day") : t("elon.tracker.days");
    return { text: `${days} ${dayLabel} ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`, days, hours, minutes, seconds, totalMs: diff };
  }, [activeMarket, t]);

  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining());

  useEffect(() => {
    const interval = setInterval(() => setTimeRemaining(getTimeRemaining()), 1000);
    return () => clearInterval(interval);
  }, [getTimeRemaining]);

  // Format time helper
  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Format date for display
  const formatDateDisplay = (timestamp: number) => {
    const d = new Date(timestamp);
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    const hours = d.getHours().toString().padStart(2, "0");
    const mins = d.getMinutes().toString().padStart(2, "0");
    const secs = d.getSeconds().toString().padStart(2, "0");
    return `${month}-${day} ${hours}:${mins}:${secs}`;
  };

  if (loading) {
    return (
      <div className="w-full py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-white/10 rounded-[5px] w-1/3" />
            <div className="h-24 bg-white/10 rounded-[5px]" />
            <div className="h-64 bg-white/10 rounded-[5px]" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full py-6">
        <div className="max-w-[1800px] mx-auto text-center py-12">
          <p className="text-red-400 text-lg">{error}</p>
          <button onClick={fetchData} className="mt-4 px-4 py-2 bg-[#CCFF00] text-black rounded-[5px] font-bold">
            {t("elon.tracker.retry")}
          </button>
        </div>
      </div>
    );
  }

  const totalTweets = data?.metrics?.totalTweets || 0;
  const dailyMetrics = data?.metrics?.daily || [];
  const hourlyMetrics = data?.metrics?.hourly || [];

  // Calculate progress and pace
  const marketStart = new Date(activeMarket.startDate + "T17:00:00Z");
  const marketEnd = new Date(activeMarket.endTime);
  const totalDuration = marketEnd.getTime() - marketStart.getTime();
  const elapsed = Date.now() - marketStart.getTime();
  const timeProgress = Math.min(Math.max(elapsed / totalDuration, 0), 1);
  const progressPercent = (totalTweets / 280) * 100;
  const pace = timeProgress > 0 ? Math.round(totalTweets / timeProgress) : 0;

  // Days for progress bar
  const progressDays = ["Fri", "Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"];

  // Build heatmap
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const getUserTimezone = () => {
    const offset = new Date().getTimezoneOffset();
    const sign = offset <= 0 ? "+" : "-";
    const absOffset = Math.abs(offset);
    const h = Math.floor(absOffset / 60).toString().padStart(2, "0");
    const m = (absOffset % 60).toString().padStart(2, "0");
    return `GMT${sign}${h}:${m}`;
  };

  // All tweets for recent posts
  const allTweets = liveData?.tweets || [];
  const totalPages = Math.ceil(allTweets.length / postsPerPage);
  const paginatedTweets = allTweets.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);

  return (
    <div className="w-full pb-24 lg:pb-8 min-h-screen">
      <div className="max-w-[1800px] mx-auto space-y-6">
        {/* Header with controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <RefreshCw className={`w-4 h-4 ${countdown < 5 ? "animate-spin" : ""}`} />
              <span>{countdown}s</span>
            </div>
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`flex items-center gap-2 px-3 py-2 rounded-[5px] text-sm transition-colors ${
                soundEnabled ? "bg-[#CCFF00]/20 text-[#CCFF00]" : "bg-white/10 text-gray-400"
              }`}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Current week banner — single label since the backend only serves
            the live week's stats (no historical range support). */}
        <div className="flex items-center justify-center border border-white/20 rounded-[5px] bg-[#1a1a1a] py-3 px-6">
          <span className="text-sm font-medium text-white border-b-2 border-[#CCFF00] pb-1">
            {(() => {
              // Build a long, natural date label per locale and capitalize
              // weekday + month parts (Spanish lowercases them by default).
              // ES: "Viernes 8 de Mayo" / EN: "Friday, May 8".
              const parts = new Intl.DateTimeFormat(locale, {
                weekday: "long",
                month: "long",
                day: "numeric",
              }).formatToParts(new Date(activeMarket.endTime));
              const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
              let out = parts
                .map((p) => (p.type === "weekday" || p.type === "month" ? cap(p.value) : p.value))
                .join("");
              // Spanish formatter inserts ", " after the weekday — drop it
              // for the cleaner "Viernes 8 de Mayo" look.
              if (locale === "es") out = out.replace(/^(\S+),\s*/, "$1 ");
              return out;
            })()}
          </span>
        </div>

        {/* New Tweet Alert */}
        {newTweetAlert && liveData?.latestTweet && (
          <div className="animate-pulse">
            <div className="bg-gradient-to-r from-[#CCFF00]/20 via-[#CCFF00]/10 to-[#CCFF00]/20 border border-[#CCFF00] rounded-[5px] p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-[#CCFF00] rounded-[5px] animate-bounce">
                  <Bell className="w-5 h-5 text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[#CCFF00] font-bold text-sm">{t("elon.tracker.newTweet")}</span>
                  </div>
                  <p className="text-white text-sm line-clamp-2">{liveData.latestTweet.content}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Stats */}
        <div className="bg-[#111] border border-white/10 rounded-[5px] p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
            <div className="text-center">
              <div className="text-gray-500 text-sm mb-1">{t("elon.tracker.tweetCount")}</div>
              <div className="text-4xl font-monument font-black text-white">{totalTweets}</div>
            </div>
            <div className="text-center">
              <div className="text-gray-500 text-sm mb-1">{t("elon.tracker.timeLeft")}</div>
              <div className="text-4xl font-monument font-black text-white">{timeRemaining.text}</div>
            </div>
            <div className="text-center md:col-span-2">
              <div className="text-gray-500 text-sm mb-1">{t("elon.tracker.progress")}</div>
              <div className="text-4xl font-monument font-black text-white">{progressPercent.toFixed(1)}%</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div className="h-8 bg-white/10 rounded-[5px] overflow-hidden relative">
              {/* Orange progress */}
              <div
                className="h-full bg-gradient-to-r from-orange-600 to-orange-500 rounded-[5px] transition-all duration-500"
                style={{ width: `${Math.min(progressPercent, 100)}%` }}
              />
              {/* Pace indicator */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {t("elon.tracker.progressPace", { progress: progressPercent.toFixed(1), pace })}
                </span>
              </div>
            </div>
            {/* Day markers */}
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              {progressDays.map((day, i) => (
                <span key={i}>{day}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Speed to reach the target */}
        <div className="bg-[#111] border border-white/10 rounded-[5px] p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            {t("elon.tracker.speedTitle")}
          </h2>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-14 gap-2">
            {SPEED_TARGETS.map((target) => {
              const tweetsRemaining = target - totalTweets;
              const msRemaining = timeRemaining.totalMs;
              const isPassed = tweetsRemaining <= 0;
              const isCurrent = !isPassed && target === SPEED_TARGETS.find(t => t > totalTweets);

              let timePerTweet = "";
              if (!isPassed && msRemaining > 0 && tweetsRemaining > 0) {
                const msPerTweet = msRemaining / tweetsRemaining;
                const totalSecs = Math.floor(msPerTweet / 1000);
                const mins = Math.floor(totalSecs / 60);
                const secs = totalSecs % 60;
                timePerTweet = t("elon.tracker.timePerTweet", {
                  mins: mins.toString().padStart(2, "0"),
                  secs: secs.toString().padStart(2, "0"),
                });
              }

              return (
                <div
                  key={target}
                  className={`rounded-[5px] p-3 text-center ${
                    isPassed
                      ? "bg-green-900/30 border border-green-700/50"
                      : isCurrent
                      ? "bg-orange-900/30 border border-orange-500"
                      : "bg-white/5 border border-white/10"
                  }`}
                >
                  <div className={`font-bold text-lg ${isPassed ? "text-green-400" : isCurrent ? "text-orange-400" : "text-white"}`}>
                    {target}
                  </div>
                  {isPassed ? (
                    <div className="text-green-400 text-xs mt-1">{t("elon.tracker.pass")}</div>
                  ) : isCurrent ? (
                    <div className="text-orange-400 text-xs mt-1">{t("elon.tracker.current")}</div>
                  ) : (
                    <div className="text-gray-500 text-[10px] mt-1 leading-tight">{timePerTweet || t("elon.tracker.na")}</div>
                  )}
                  {!isPassed && tweetsRemaining > 0 && (
                    <div className="text-gray-600 text-[10px] mt-0.5">-{tweetsRemaining}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Live speed */}
        {liveData?.liveSpeed && (
          <div className="bg-[#111] border border-white/10 rounded-[5px] p-6">
            <h2 className="text-lg font-semibold text-white mb-4">{t("elon.tracker.liveSpeed")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Row 1 */}
              <div className="flex items-center justify-between bg-white/5 rounded-[5px] px-4 py-3">
                <span className="text-gray-400 text-sm">{t("elon.tracker.timeSinceLast")}</span>
                <span className="text-orange-400 font-mono font-bold">
                  {liveData.liveSpeed.timeSinceLastActivity !== null
                    ? formatTime(liveData.liveSpeed.timeSinceLastActivity)
                    : t("elon.tracker.na")}
                </span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-[5px] px-4 py-3">
                <span className="text-gray-400 text-sm">{t("elon.tracker.activityCount")}</span>
                <span className="text-white font-bold">{t("elon.tracker.activities", { count: liveData.liveSpeed.activityCountInPeriod })}</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-[5px] px-4 py-3">
                <span className="text-gray-400 text-sm">{t("elon.tracker.lastOne", { tz: getUserTimezone() })}</span>
                <span className="text-white font-mono">
                  {liveData.liveSpeed.lastActivityInPeriod
                    ? formatDateDisplay(liveData.liveSpeed.lastActivityInPeriod)
                    : t("elon.tracker.na")}
                </span>
              </div>
              {/* Row 2 */}
              <div className="flex items-center justify-between bg-white/5 rounded-[5px] px-4 py-3">
                <span className="text-gray-400 text-sm">{t("elon.tracker.postingSpeed")}</span>
                <span className="text-orange-400 font-mono font-bold">
                  {liveData.liveSpeed.avgTimeBetweenTweets !== null
                    ? t("elon.tracker.perActivity", { time: formatTime(liveData.liveSpeed.avgTimeBetweenTweets) })
                    : t("elon.tracker.na")}
                </span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-[5px] px-4 py-3">
                <span className="text-gray-400 text-sm">{t("elon.tracker.screenTime")}</span>
                <span className="text-white font-mono font-bold">
                  {liveData.liveSpeed.screenTime !== null ? formatTime(liveData.liveSpeed.screenTime) : t("elon.tracker.na")}
                </span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded-[5px] px-4 py-3">
                <span className="text-gray-400 text-sm">{t("elon.tracker.firstOne", { tz: getUserTimezone() })}</span>
                <span className="text-white font-mono">
                  {liveData.liveSpeed.firstActivityInPeriod
                    ? formatDateDisplay(liveData.liveSpeed.firstActivityInPeriod)
                    : t("elon.tracker.na")}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Tweets Heatmap */}
        <div className="bg-[#111] border border-white/10 rounded-[5px] p-6">
          {/* Header with title and controls */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">{t("elon.tracker.heatmapTitle")}</h2>
              <div className="flex items-center gap-2 px-2 py-1 bg-white/5 rounded-[5px]">
                <span className="text-gray-500 text-xs">ℹ️</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeReplies}
                  onChange={(e) => setIncludeReplies(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-transparent text-[#CCFF00] focus:ring-[#CCFF00]"
                />
                <span className="text-gray-400 text-sm">{t("elon.tracker.repliesIncluded")}</span>
              </label>
              <select className="bg-white/5 border border-white/10 rounded-[5px] px-3 py-1.5 text-gray-400 text-sm">
                <option value="">{t("elon.tracker.focusDay")}</option>
              </select>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-400">ET:</span>
              <span>{t("elon.tracker.legendET")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-400">LT:</span>
              <span>{t("elon.tracker.legendLT")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-[5px] bg-[#CCFF00]"></span>
              <span>{t("elon.tracker.legendStart")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-[5px] bg-gray-600 flex items-center justify-center text-[8px]">⏱</span>
              <span>{t("elon.tracker.legendNow")}</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                {/* ET Row */}
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 px-2 sticky left-0 bg-[#111] z-10 min-w-[80px]">
                    <div className="text-gray-500">{t("elon.tracker.colDate")}</div>
                  </th>
                  <th className="text-left text-orange-400 py-2 px-1 font-medium">ET:</th>
                  {hours.map((h) => {
                    const etHour = (h - 5 + 24) % 24;
                    return (
                      <th key={`et-${h}`} className="text-center text-gray-500 py-1 px-0.5 min-w-[32px] font-normal">
                        {etHour.toString().padStart(2, "0")}:00
                      </th>
                    );
                  })}
                  <th className="text-right text-gray-500 py-2 px-3 font-medium">{t("elon.tracker.colTotal")}</th>
                </tr>
                {/* LT Row */}
                <tr className="border-b border-white/20">
                  <th className="text-left text-gray-500 py-2 px-2 sticky left-0 bg-[#111] z-10"></th>
                  <th className="text-left text-gray-500 py-2 px-1 font-medium">LT:</th>
                  {hours.map((h) => (
                    <th key={`lt-${h}`} className="text-center text-gray-600 py-1 px-0.5 min-w-[32px] font-normal">
                      {h.toString().padStart(2, "0")}:00
                    </th>
                  ))}
                  <th className="text-right text-gray-500 py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {[...dailyMetrics].reverse().map((day, rowIdx) => {
                  const dayHourly = hourlyMetrics.filter((h) => h.date === day.date);
                  const dayName = new Date(day.date).toLocaleDateString(locale, { weekday: "short" });
                  const dateStr = `${(new Date(day.date).getMonth() + 1).toString().padStart(2, "0")}-${new Date(day.date).getDate().toString().padStart(2, "0")} ${dayName}`;

                  // Check if this is the market's start day (week boundary).
                  const isMarketStartDay = day.date === activeMarket.startDate;
                  // Market starts at 17:00 UTC (12:00 ET)
                  const marketStartHourUTC = 17;

                  // Check if this is today
                  const today = new Date().toISOString().split("T")[0];
                  const isToday = day.date === today;
                  const currentHourUTC = new Date().getUTCHours();

                  return (
                    <tr key={day.date} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                      <td className="text-gray-400 py-1 px-2 sticky left-0 bg-[#111] whitespace-nowrap z-10 font-medium">
                        {dateStr}
                      </td>
                      <td className="py-1 px-1"></td>
                      {hours.map((h) => {
                        const hourStr = `${h.toString().padStart(2, "0")}:00`;
                        const found = dayHourly.find((dh) => dh.hour === hourStr);
                        const count = includeReplies ? (found?.total || 0) : (found?.tweets || 0);
                        const hasActivity = count > 0;

                        // Check for market start indicator
                        const isMarketStart = isMarketStartDay && h === marketStartHourUTC;
                        // Check for current time indicator
                        const isCurrentTime = isToday && h === currentHourUTC;

                        return (
                          <td key={h} className="py-0.5 px-0.5 relative">
                            <div
                              className={`w-8 h-7 rounded flex items-center justify-center text-[10px] font-bold relative ${
                                hasActivity
                                  ? "bg-orange-500 text-white shadow-sm"
                                  : "bg-[#1a3a4a]"
                              }`}
                            >
                              {hasActivity ? count : ""}
                              {/* Market start indicator */}
                              {isMarketStart && (
                                <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-[5px] bg-[#CCFF00]"></span>
                              )}
                              {/* Current time indicator */}
                              {isCurrentTime && (
                                <span className="absolute -top-1 left-1/2 transform -translate-x-1/2 text-[8px]">⏱</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="text-right text-[#CCFF00] font-bold py-1 px-3 tabular-nums">
                        {includeReplies ? day.total : day.tweets}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer note */}
          <div className="mt-4 text-xs text-gray-600 text-right">
            {t("elon.tracker.refreshNote")}
          </div>
        </div>

        {/* Recent Posts */}
        <div className="bg-[#111] border border-white/10 rounded-[5px] p-6">
          <h2 className="text-lg font-semibold text-white mb-4">{t("elon.tracker.recentPosts")}</h2>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-500 py-3 px-3 font-medium">{t("elon.tracker.colCount")}</th>
                  <th className="text-left text-gray-500 py-3 px-3 font-medium">{t("elon.tracker.colTimestamp")}</th>
                  <th className="text-left text-gray-500 py-3 px-3 font-medium">{t("elon.tracker.colType")}</th>
                  <th className="text-left text-gray-500 py-3 px-3 font-medium">{t("elon.tracker.colId")}</th>
                  <th className="text-left text-gray-500 py-3 px-3 font-medium">{t("elon.tracker.colContent")}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTweets.map((tweet, i) => (
                  <tr key={tweet.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="py-3 px-3 text-white font-medium">
                      {tweet.isReply ? "0" : tweet.tweetNumber || t("elon.tracker.na")}
                    </td>
                    <td className="py-3 px-3 text-gray-400 font-mono text-xs">
                      {new Date(tweet.timestamp).toISOString().slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="py-3 px-3">
                      {tweet.isReply ? (
                        <span className="text-gray-400">{t("elon.tracker.typeReply")}</span>
                      ) : (
                        <span className="text-blue-400">{t("elon.tracker.typeTweet")}</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-gray-500 font-mono text-xs">
                      <div>{tweet.id}</div>
                      <div className="text-gray-600">{tweet.isReply ? t("elon.tracker.none") : tweet.id}</div>
                    </td>
                    <td className="py-3 px-3 text-white max-w-md">
                      <a
                        href={`https://x.com/elonmusk/status/${tweet.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[#CCFF00] transition-colors line-clamp-2"
                      >
                        {tweet.content}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-[5px] bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-[5px] text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-[#CCFF00] text-black"
                        : "bg-white/5 hover:bg-white/10 text-white"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="text-gray-500">...</span>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="w-8 h-8 rounded-[5px] text-sm font-medium bg-white/5 hover:bg-white/10 text-white"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-[5px] bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              <span className="text-gray-500 text-sm ml-2">{t("elon.tracker.perPage", { n: postsPerPage })}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-gray-600 text-xs">
          {t("elon.tracker.lastUpdated", { time: data?.fetchedAt ? new Date(data.fetchedAt).toLocaleTimeString(locale) : t("elon.tracker.na") })}
        </div>
      </div>
    </div>
  );
}
